import React from "react";
import type { OrderEnvelope } from "@tilt/pos-sdk";
import { useTranslation } from "react-i18next";

import { formatCents, formatDateTime } from "../../lib/format";
import { useActiveTimezone } from "../../lib/useActiveTimezone";
import { cn } from "../../lib/utils";

export interface ReceiptProps {
  readonly envelope: OrderEnvelope;
  /** Merged brand cascade - used for the header logo + product name +
   *  receipt footer text + powered-by attribution. */
  readonly brand: Record<string, unknown> | undefined | null;
  /** "merchant" copy includes operator details (cashier name, terminal
   *  id) the customer doesn't need; "customer" copy is leaner. */
  readonly variant?: "customer" | "merchant";
  /** When set, renders only the single payment with this ID (for per-payment print). */
  readonly paymentId?: string;
  readonly className?: string;
}

/** Branded receipt - printable + emailable. The layout is monospace
 *  inside a fixed-width card so it reads identically on screen, in a
 *  print preview, or pasted into an email body. */
export function Receipt({
  envelope,
  brand,
  variant = "customer",
  paymentId,
  className,
}: ReceiptProps) {
  const { t } = useTranslation();
  const tz = useActiveTimezone();
  const { order, payments } = envelope;

  const productName = pickStr(brand, "product_name") ?? "POS";
  const logoUrl = pickStr(brand, "logo_url");
  const receiptHeader = pickStr(brand, "receipt_header");
  const receiptFooter = pickStr(brand, "receipt_footer");
  const poweredByOn = brand?.powered_by_enabled === true;
  const poweredByText = pickStr(brand, "powered_by_text");

  // When paymentId is set, scope to just that payment; otherwise show all.
  const allPayments = paymentId ? payments.filter((p) => p.payment_id === paymentId) : payments;
  const charges = allPayments.filter((p) => p.role === "charge" && !p.cancelled_at);
  const refunds = allPayments.filter((p) => p.role === "refund" && !p.cancelled_at);

  return (
    <article
      className={cn(
        "mx-auto max-w-md rounded-xl border border-line bg-surface p-6 font-mono text-[0.8125rem] leading-[1.5] text-strong",
        "print:border-0 print:p-0 print:max-w-none print:rounded-none",
        className,
      )}
    >
      {/* ----- Header ----- */}
      <header className="text-center font-sans">
        {logoUrl && (
          <img
            src={logoUrl}
            alt={productName}
            className="mx-auto mb-2 h-12 w-auto object-contain"
          />
        )}
        <h1 className="text-base font-semibold tracking-tight text-strong">{productName}</h1>
        {receiptHeader && (
          <p className="mt-0.5 whitespace-pre-line text-xs text-muted">{receiptHeader}</p>
        )}
      </header>

      <Divider />

      {/* ----- Meta ----- */}
      <div className="space-y-0.5 text-xs">
        <Row
          label={t("receipt.orderId")}
          value={order.id.split("-")[0]?.toUpperCase() ?? order.id}
        />
        {order.external_reference && (
          <Row label={t("receipt.reference")} value={order.external_reference} />
        )}
        <Row label={t("receipt.dateTime")} value={formatDateTime(order.created_at, tz)} />
        {order.customer_name && <Row label={t("receipt.customer")} value={order.customer_name} />}
        {variant === "merchant" && order.opened_by_membership_id && (
          <Row
            label={t("receipt.cashier")}
            value={order.opened_by_membership_id.split("-")[0]?.toUpperCase() ?? ""}
          />
        )}
      </div>

      {order.description && (
        <>
          <Divider />
          <p className="whitespace-pre-line text-xs text-foreground">{order.description}</p>
        </>
      )}

      <Divider />

      {/* ----- Amount block ----- */}
      <div className="space-y-0.5">
        {order.discount_cents > 0 ? (
          <>
            <Row
              label={t("receipt.subtotal")}
              value={formatCents(order.total_cents, order.currency)}
            />
            {(order.discounts ?? []).map((d) => (
              <Row
                key={d.id}
                label={
                  d.percent_bps != null
                    ? t("receipt.discountWithPercent", {
                        label: d.discount_type_display_name,
                        percent: (d.percent_bps / 100).toFixed(d.percent_bps % 100 === 0 ? 0 : 2),
                      })
                    : d.discount_type_display_name
                }
                value={`-${formatCents(d.amount_cents, order.currency)}`}
                tone="muted"
              />
            ))}
            {(order.discounts ?? []).length === 0 && (
              <Row
                label={order.discount_label}
                value={`-${formatCents(order.discount_cents, order.currency)}`}
                tone="muted"
              />
            )}
            <Row
              label={t("receipt.totalDue")}
              value={formatCents(
                Math.max(0, order.total_cents - order.discount_cents),
                order.currency,
              )}
            />
          </>
        ) : (
          <Row
            label={t("receipt.totalDue")}
            value={formatCents(order.total_cents, order.currency)}
          />
        )}
        {order.surcharge_cents > 0 && (
          <Row
            label={t("receipt.surcharge")}
            value={formatCents(order.surcharge_cents, order.currency)}
          />
        )}
      </div>

      <Divider />

      {/* ----- Payments ----- */}
      <div className="space-y-0.5 text-xs">
        <div className="text-[0.6875rem] uppercase tracking-wider text-muted">
          {t("receipt.tender")}
        </div>
        {charges.length === 0 ? (
          <div className="text-muted">{t("receipt.noPayments")}</div>
        ) : (
          charges.map((p) => (
            <React.Fragment key={p.link_id}>
              <Row
                label={`${capitalise(p.payment_type)}${p.external_ref ? ` · ${p.external_ref.slice(-6)}` : ""}`}
                value={formatCents(p.applied_amount_cents, order.currency)}
                tone={p.status === "PENDING" ? "muted" : "default"}
                suffix={p.status === "PENDING" ? ` (${t("receipt.pending")})` : ""}
              />
              {p.surcharge_cents > 0 && (
                <Row
                  label={`  ${t("receipt.surcharge")}`}
                  value={formatCents(p.surcharge_cents, order.currency)}
                  tone="muted"
                />
              )}
            </React.Fragment>
          ))
        )}
        {refunds.length > 0 && (
          <>
            <div className="mt-2 text-[0.6875rem] uppercase tracking-wider text-muted">
              {t("receipt.refunds")}
            </div>
            {refunds.map((p) => (
              <Row
                key={p.link_id}
                label={`${capitalise(p.payment_type)} ${t("receipt.refund")}`}
                value={`−${formatCents(p.applied_amount_cents, order.currency)}`}
                tone="muted"
              />
            ))}
          </>
        )}
      </div>

      <Divider />

      {/* ----- Totals ----- */}
      <div className="space-y-0.5 text-sm font-semibold">
        <Row label={t("receipt.paid")} value={formatCents(order.paid_cents, order.currency)} />
        {order.refunded_cents > 0 && (
          <Row
            label={t("receipt.refunded")}
            value={formatCents(order.refunded_cents, order.currency)}
          />
        )}
        <Row
          label={t("receipt.balance")}
          value={formatCents(order.remaining_cents, order.currency)}
          tone={order.remaining_cents === 0 ? "success" : "warn"}
        />
      </div>

      {/* ----- Footer ----- */}
      {(receiptFooter || (poweredByOn && poweredByText)) && (
        <>
          <Divider />
          <div className="space-y-1.5 text-center font-sans text-[0.6875rem] text-muted">
            {receiptFooter && <p className="whitespace-pre-line">{receiptFooter}</p>}
            {poweredByOn && poweredByText && <p className="text-faint">{poweredByText}</p>}
          </div>
        </>
      )}
    </article>
  );
}

function Row({
  label,
  value,
  tone,
  suffix,
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: "default" | "muted" | "success" | "warn";
  readonly suffix?: string;
}) {
  const toneCls =
    tone === "muted"
      ? "text-muted"
      : tone === "success"
        ? "text-emerald-700"
        : tone === "warn"
          ? "text-amber-700"
          : "text-strong";
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={cn("truncate text-foreground", tone === "muted" && "text-faint")}>
        {label}
      </span>
      <span className={cn("tabular-nums", toneCls)}>
        {value}
        {suffix}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="my-3 border-t border-dashed border-line" aria-hidden="true" />;
}

function pickStr(obj: Record<string, unknown> | null | undefined, key: string): string | null {
  if (!obj) return null;
  const v = obj[key];
  return typeof v === "string" && v.trim().length > 0 ? v : null;
}

function capitalise(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}
